import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { FOUNDATION_INFO } from "@/lib/initiatives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, IndianRupee, Building2, User, ArrowRight, Loader2 } from "lucide-react";
import DonationFAQ from "@/components/DonationFAQ";

const PRESETS = [500, 1000, 2500, 5000, 10000, 25000];

export default function Donate() {
  const [params] = useSearchParams();
  const causeFromUrl = params.get("cause") || "General";
  const navigate = useNavigate();

  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [isCorporate, setIsCorporate] = useState(false);
  const [form, setForm] = useState({
    donor_name: "",
    donor_email: "",
    donor_phone: "",
    donor_pan: "",
    donor_address: "",
    company_name: "",
    cause: causeFromUrl,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const finalAmount = useMemo(() => {
    const c = parseFloat(custom);
    if (!Number.isNaN(c) && c > 0) return c;
    return amount;
  }, [amount, custom]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 1) {
      toast.error("Please enter a valid donation amount");
      return;
    }
    if (!form.donor_name || !form.donor_email || !form.donor_phone) {
      toast.error("Name, email and phone are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        donor_name: form.donor_name,
        donor_email: form.donor_email,
        donor_phone: form.donor_phone,
        donor_pan: form.donor_pan || null,
        donor_address: form.donor_address || null,
        amount: finalAmount,
        cause: form.cause || "General",
        is_corporate: isCorporate,
        company_name: isCorporate ? form.company_name : null,
      };
      const { data: order } = await api.post("/donations/order", payload);

      // ---- MOCK RAZORPAY CHECKOUT ----
      // In production, Razorpay Checkout would open here. We simulate it.
      toast.message("Opening Razorpay checkout (mock)…");
      await new Promise((r) => setTimeout(r, 1200));

      const { data: verified } = await api.post("/donations/verify", {
        donation_id: order.donation_id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
        razorpay_order_id: order.order_id,
        razorpay_signature: "mock_signature",
      });

      navigate(`/donation/success?id=${verified.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Donation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-py">
      <div className="mx-auto max-w-7xl container-x grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">Donate & Become a UB Mitra</div>
          <h1 className="mt-3 font-serif-display text-4xl md:text-5xl text-[#0A2540]">
            Every contribution creates lasting change.
          </h1>
          <p className="mt-4 text-[#4A5568] max-w-2xl">
            You will receive an official receipt and 80G certificate details by email
            immediately after a successful donation.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-8" data-testid="donate-form">
            {/* Donor type toggle */}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="donor-type-individual"
                onClick={() => setIsCorporate(false)}
                className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  !isCorporate
                    ? "bg-[#0A2540] text-white border-[#0A2540]"
                    : "bg-white text-[#0A2540] border-[#0A2540]/20 hover:border-[#0A2540]"
                }`}
              >
                <User className="w-4 h-4" /> Individual
              </button>
              <button
                type="button"
                data-testid="donor-type-corporate"
                onClick={() => setIsCorporate(true)}
                className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  isCorporate
                    ? "bg-[#0A2540] text-white border-[#0A2540]"
                    : "bg-white text-[#0A2540] border-[#0A2540]/20 hover:border-[#0A2540]"
                }`}
              >
                <Building2 className="w-4 h-4" /> Company / CSR
              </button>
            </div>

            {/* Amount */}
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#4A5568]">
                Choose an amount (INR)
              </Label>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`amount-${p}`}
                    onClick={() => {
                      setAmount(p);
                      setCustom("");
                    }}
                    className={`rounded-full px-3 py-2.5 text-sm font-medium border transition-colors ${
                      finalAmount === p && !custom
                        ? "bg-[#D4AF37] text-[#0A2540] border-[#D4AF37]"
                        : "bg-white text-[#0A2540] border-[#0A2540]/15 hover:border-[#0A2540]"
                    }`}
                  >
                    ₹{p.toLocaleString("en-IN")}
                  </button>
                ))}
              </div>
              <div className="mt-4 relative">
                <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
                <Input
                  data-testid="custom-amount-input"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Enter custom amount"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="pl-9 h-12"
                />
              </div>
            </div>

            {/* Donor info */}
            <div className="grid md:grid-cols-2 gap-4">
              {isCorporate && (
                <div className="md:col-span-2">
                  <Label>Company name*</Label>
                  <Input
                    required
                    value={form.company_name}
                    onChange={set("company_name")}
                    data-testid="company-name-input"
                    className="mt-2 h-11"
                  />
                </div>
              )}
              <div>
                <Label>{isCorporate ? "Contact person*" : "Full name*"}</Label>
                <Input
                  required
                  value={form.donor_name}
                  onChange={set("donor_name")}
                  data-testid="donor-name-input"
                  className="mt-2 h-11"
                />
              </div>
              <div>
                <Label>Email*</Label>
                <Input
                  required
                  type="email"
                  value={form.donor_email}
                  onChange={set("donor_email")}
                  data-testid="donor-email-input"
                  className="mt-2 h-11"
                />
              </div>
              <div>
                <Label>Phone*</Label>
                <Input
                  required
                  value={form.donor_phone}
                  onChange={set("donor_phone")}
                  data-testid="donor-phone-input"
                  className="mt-2 h-11"
                />
              </div>
              <div>
                <Label>PAN (for 80G receipt)</Label>
                <Input
                  value={form.donor_pan}
                  onChange={set("donor_pan")}
                  data-testid="donor-pan-input"
                  className="mt-2 h-11"
                  placeholder="AAAAA0000A"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Address (optional)</Label>
                <Textarea
                  value={form.donor_address}
                  onChange={set("donor_address")}
                  rows={2}
                  data-testid="donor-address-input"
                  className="mt-2"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Which cause is closest to your heart?</Label>
                <Input
                  value={form.cause}
                  onChange={set("cause")}
                  data-testid="donor-cause-input"
                  className="mt-2 h-11"
                />
              </div>
            </div>

            {/* CTA */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                data-testid="donate-submit-btn"
                className="rounded-full bg-[#0A2540] text-white hover:bg-[#D4AF37] hover:text-[#0A2540] h-13 px-8 py-6 text-base font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…
                  </>
                ) : (
                  <>
                    Donate ₹{Number(finalAmount).toLocaleString("en-IN")} securely
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              <p className="mt-3 text-xs text-[#4A5568]">
                Payments are processed via Razorpay (test mode). Your receipt & 80G
                details will be emailed instantly.
              </p>
            </div>
          </form>
        </div>

        {/* Sidebar: bank + 80G details */}
        <aside className="lg:col-span-5 space-y-6">
          <div className="rounded-3xl bg-[#0A2540] text-white p-8">
            <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.24em]">
              <ShieldCheck className="w-4 h-4" /> 80G Tax Benefit
            </div>
            <h3 className="mt-3 font-serif-display text-2xl">
              Save tax. Do good. Both.
            </h3>
            <p className="mt-3 text-white/70 text-sm">
              UB Maitreya Foundation is registered under section 12AB and approved under
              80G of the Income Tax Act, 1961.
            </p>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">80G Approval No.</span>
                <span>{FOUNDATION_INFO.reg80g}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Approval Date</span>
                <span>{FOUNDATION_INFO.reg80g_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Valid AY</span>
                <span>{FOUNDATION_INFO.ay_validity}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">PAN</span>
                <span>{FOUNDATION_INFO.pan}</span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-white border border-[#0A2540]/10 p-8">
            <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">
              Direct bank transfer
            </div>
            <h3 className="mt-3 font-serif-display text-2xl text-[#0A2540]">
              Prefer a bank transfer?
            </h3>
            <div className="mt-5 space-y-3 text-sm">
              <div>
                <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">Account name</div>
                <div className="font-medium text-[#0A2540]">{FOUNDATION_INFO.name}</div>
              </div>
              <div>
                <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">Bank</div>
                <div className="font-medium text-[#0A2540]">{FOUNDATION_INFO.bank}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">A/c No.</div>
                  <div className="font-medium text-[#0A2540]">{FOUNDATION_INFO.account}</div>
                </div>
                <div>
                  <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">IFSC</div>
                  <div className="font-medium text-[#0A2540]">{FOUNDATION_INFO.ifsc}</div>
                </div>
              </div>
              <div>
                <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">Branch</div>
                <div className="font-medium text-[#0A2540]">{FOUNDATION_INFO.branch}</div>
              </div>
            </div>
            <p className="mt-6 text-xs text-[#4A5568]">
              After a bank transfer, please email your remittance receipt with PAN and
              address to{" "}
              <Link to="/contact" className="text-[#0A2540] underline">
                us
              </Link>{" "}
              — we will send your 80G certificate.
            </p>
          </div>
        </aside>
      </div>
      <DonationFAQ />
    </section>
  );
}
